"use client";

import { ArrowRight } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Eligibility() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: listRef,
    isVisible: listVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const faqs = [{
    question: "What are the credit score requirements for JV Partners?",
    answer: "Our Joint Venture program requires a minimum credit score of 700. We look for partners who demonstrate financial responsibility and a track record of managing capital effectively."
  }, {
    question: "Do you fund projects outside of major metropolitan areas?",
    answer: "We evaluate deals based on market viability and asset performance. While we focus on high-growth real estate markets, we are open to reviewing projects in secondary markets that demonstrate strong rental demand or flip potential."
  }, {
    question: "How does the gap equity funding work?",
    answer: "Our gap equity provides second-position financing to bridge the difference between your first mortgage and the total capital required. This allows you to close deals with minimal out-of-pocket cash, maximizing your leverage."
  }, {
    question: "What documentation is required for pre-qualification?",
    answer: "Property Address, Purchase Price, Rehab Amount, After Repair Value, Experience: Number of rehabs completed in the last 3 years, Credit Score."
  }, {
    question: "Are there specific property types you avoid?",
    answer: "We specialize in residential real estate, including Fix-and-Flip, BRRRR, Buy-and-Hold, and New Construction. We generally do not fund raw land or highly specialized commercial assets that fall outside our core expertise."
  }, {
    question: "How long does the funding process take?",
    answer: "Once we have a complete file and a clear scope of work, we can move from initial review to funding in as little as 7-10 business days. Our process is designed for speed and institutional reliability."
  }, {
    question: "Can referral partners track their deal status?",
    answer: "Yes. Our Referral Partner portal provides real-time updates on the status of your referred deals, from initial submission through to closing and commission disbursement."
  }, {
    question: "What makes BeneWealth different from a traditional bank?",
    answer: "Unlike traditional banks, we are architects of capital. We don't rely on rigid, slow-moving underwriting. We provide flexible, deal-specific funding solutions designed to help real estate investors scale their portfolios rapidly."
  }];
  return <section data-section-id="381" id="eligibility-faq" className="bg-background py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div ref={headerRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 mb-16 md:mb-24 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="lg:col-span-7">
            <p className="font-default text-xs tracking-[0.15em] text-slate-muted uppercase mb-6">
              Technical Specifications
            </p>
            <h2 className="font-default text-5xl md:text-6xl lg:text-7xl leading-[1.05] text-foreground tracking-tighter">
              Funding
              <br />
              <em className="italic text-primary font-normal">Criteria</em>
            </h2>
          </div>

          <div className="lg:col-span-5 lg:pt-8 flex flex-col justify-end">
            <p className="font-default text-base md:text-lg text-slate-muted leading-relaxed max-w-md mb-6">
              Professional‑grade answers to your most pressing questions about our capital engines.
            </p>
            <Link to="/PreQualificationFormPage" className="inline-flex items-center gap-2 font-default text-xs tracking-[0.15em] uppercase text-foreground hover:text-primary transition-colors duration-300 w-fit group">
              Get Pre-Qualified
              <ArrowRight className="size-3 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </div>

        <div ref={listRef} className={`max-w-[800px] mx-auto transition-all duration-700 ease-out ${listVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, i) => <AccordionItem key={i} data-index={i} value={`item-${i}`} className="border-t border-border last:border-b border-b-0 data-[state=open]:border-foreground/20 group">
                <AccordionTrigger className="py-6 md:py-7 hover:no-underline [&>svg]:hidden text-left group/trigger">
                  <span className="font-default text-lg md:text-xl leading-snug text-foreground transition-colors duration-300 group-data-[state=open]:italic group-data-[state=open]:text-primary pr-6">
                    {faq.question}
                  </span>
                  <span className="relative flex items-center justify-center size-6 shrink-0 mt-1">
                    <span className="absolute h-px w-4 bg-foreground transition-colors duration-300 group-data-[state=open]:bg-primary" />
                    <span className="absolute h-4 w-px bg-foreground transition-all duration-300 group-data-[state=open]:rotate-90 group-data-[state=open]:opacity-0 group-data-[state=open]:bg-primary" />
                  </span>
                </AccordionTrigger>
                <AccordionContent className="pb-7 pt-0 pr-12">
                  <p className="font-default text-base leading-[1.7] text-slate-muted">
                    {faq.answer}
                  </p>
                </AccordionContent>
              </AccordionItem>)}
          </Accordion>
        </div>
      </div>
    </section>;
}